import { DatePipe, DecimalPipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { SimuladorGoApi } from '@gestor-web/@core/api/go';
import {
  DetalheAtributosQualidade,
  DetalheBeneficiario,
  DetalheGuiaCriticasGuia,
  DetalheGuiaDadosInternacao,
  DetalheGuiaDadosPrincipais,
  DetalheGuiaItensGuia,
  DetalheGuiaValoresProcessamento,
  DetalheMovimentacaoGuia,
  DetalheGuiaItensGuiaCriticasItem,
  DetalheGuiaItensGuiaDetalhePacote,
  DetalheGuiaItensGuiaMotivosRejeicaoItem,
  DetalheGuiaLancamentosGuias,
  DetalheGuiaModeloRemuneracao,
  DetalheGuiaMotivosRejeicaoGuia,
  DetalhePrestador,
  DetalhePrestadorCnes,
  DetalhePrestadorMedicos,
  DetalhePrestadorRps,
  Operadora,
  UsuarioPerfil,
  VinculoUsuarioOperadora,
} from '@gestor-web/@core/api/ords/model';
import { OperadorasSelectors, UserProfileSelectors } from '@gestor-web/@core/store/selectors';
import { FUNCIONALIDADE } from '@gestor-web/@core/auth/funcionalidades';
import { isoDateNoPuncts, pseudonimizar } from '@gestor-web/@core/utils';
import { Store } from '@ngrx/store';
import { CpfCnpjPipe } from '@gestor-web/app/shared/pipes/cpf-ou-cnpj.pipe';
import { GeralService, NotificationService, PermissaoService } from '@gestor-web/app/shared/services';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridOptions } from 'ag-grid-community';
import { exportMultipleSheetsAsExcel } from 'ag-grid-enterprise';
import { Subject, combineLatest, takeUntil } from 'rxjs';
import { AgGridActionCellParams, AgGridActionCellRenderer } from '../ag-grid-action-cell/ag-grid-action-cell.component';
import { buildGridOptions, exportGridAsExcel } from '../ag-grid-utils/ag-grid.factory';
import { AgFilterUtils } from '../ag-grid-utils/ag-grid.filter.utils';
import { ItemDetalheCellRenderer } from './components/item-detalhe/item-detalhe-cell.component';
import { ItemGroupCellRenderer } from './components/item-group/item-group-cell.component';
import { RowAtributosQualidade } from '../detalhe-prestador/detalhe-prestador.component';
import { CAMPOS_DE_DATA, DOMINIOS_MAPEADOS, CAMPO_ALIASES, GRUPOS_MOCK, CAMPOS } from './detalhes-guia.utils';

const HEIGHT_PADDING = '250px';

const COLUMNS_MODELO_REMUNERACAO: ColDef[] = [
  {
    headerName: 'Nr. registro item',
    field: 'nr_registro_item',
    width: 145,
  },
  {
    headerName: '122-Modelo de remuneração',
    field: 'nm_id_modelo_remuneracao',
    width: 250,
  },
  {
    headerName: '128-Valor informado do modelo de remuneração',
    field: 'vl_modelo_informado',
    width: 200,
    type: 'money',
  },
];

const COLUMNS_LANCAMENTOS_GUIA: ColDef[] = [
  {
    headerName: '003-Competência',
    field: 'nr_competencia',
    width: 120,
  },
  {
    headerName: 'Nr. seq. guia',
    field: 'nr_seq_guia',
  },
  {
    headerName: 'Cód. lancto',
    field: 'cd_guia_monitor',
    cellRenderer: AgGridActionCellRenderer,
    cellRendererParams: {
      actionTooltip: 'Exibir detalhes da guia',
    } as AgGridActionCellParams,
    type: 'str',
  },
  {
    headerName: '058-Valor total glosa',
    field: 'vl_glosa',
    type: 'money',
  },
  {
    headerName: '059-Valor total pago',
    field: 'vl_pago',
    type: 'money',
  },
];

const COLUMNS_CRITICAS_GUIA: ColDef[] = [
  {
    headerName: 'Cód. crítica',
    field: 'cd_critica',
  },
  {
    headerName: 'Bloco info.',
    field: 'ds_bloco_info',
  },
  {
    headerName: 'Campo na ANS',
    field: 'nm_campo_ans',
  },
  {
    headerName: 'Descrição da crítica',
    field: 'ds_critica',
    wrapText: true,
    autoHeight: true,
    width: 500,
  },
  {
    headerName: 'Tipo da crítica',
    field: 'nm_tp_critica',
  },
  {
    headerName: 'Tipo de impacto',
    field: 'nm_tp_impacto',
  },
];

const COLUMNS_MOTIVOS_REJEICAO: ColDef[] = [
  {
    headerName: 'Campo',
    field: 'campo',
  },
  {
    headerName: 'Motivos de rejeição',
    field: 'motivo_rejeicao',
    wrapText: true,
    autoHeight: true,
    width: 500,
  },
];

const COLUMNS_ITENS_GUIAS: ColDef[] = [
  {
    headerName: 'Nr. item guia',
    field: 'nr_item_guia',
    cellRenderer: 'agGroupCellRenderer', // necessário para expandir os detalhes
    cellRendererParams: {
      innerRenderer: ItemGroupCellRenderer,
    },
  },
  {
    headerName: '064-Tabela TUSS do item',
    field: 'cd_tabela_tuss',
  },
  {
    headerName: '065-Cód. grupo TUSS',
    field: 'cd_grupo_tuss',
  },
  {
    headerName: '066-Cód. procedimento ou item',
    field: 'cd_proced_item',
  },
  {
    headerName: 'Procedimento item',
    field: 'nm_proced_item',
  },
  {
    headerName: '067-ID dente',
    field: 'cd_dente',
  },
  {
    headerName: '068-ID região da boca',
    field: 'cd_regiao_boca',
  },
  {
    headerName: '069-ID face do dente',
    field: 'id_face_dente',
  },
  {
    headerName: '070-Qtd. procedimento ou item',
    field: 'qt_item_informado',
    type: 'int',
  },
  {
    headerName: '071-Valor informado do item',
    field: 'vl_item_informado',
    type: 'money',
  },
  {
    headerName: '072-Qtd. paga do item',
    field: 'qt_item_pago',
    type: 'int',
  },
  {
    headerName: '073-Valor pago ao prestador ou reembolso',
    field: 'vl_item_pago',
    type: 'money',
  },
  {
    headerName: 'Qtde. paga acumulada do item',
    field: 'qt_item_pago_acumulado',
    type: 'int',
  },
  {
    headerName: 'Valor pago acumulado do item',
    field: 'vl_item_pago_acumulado',
    type: 'money',
  },
  {
    headerName: '125-Unidade de medida',
    field: 'cd_unidade_medida',
  },
  {
    headerName: '085-Valor coparticipação item',
    field: 'vl_copartic_item',
    type: 'money',
  },
  {
    headerName: '075-Nr. CNPJ fornecedor',
    field: 'nr_cnpj_fornecedor',
    type: 'cnpj',
  },
  {
    headerName: '074-Valor pago ao fornecedor',
    field: 'vl_pago_fornecedor_item',
    type: 'money',
  },
  {
    headerName: 'Sigla classe',
    field: 'sg_classe',
  },
  {
    headerName: 'Qtd. registro pacote',
    field: 'qt_registro_pacote',
    type: 'int',
  },
];

const COLUMNS_DIALOG_BENEFICIARIO: ColDef[] = [
  {
    headerName: 'Cód. registro ANS',
    field: 'cd_registro_ans',
  },
  {
    headerName: 'Cód. controle operacional',
    field: 'nr_cco',
  },
  {
    headerName: 'Nr. CPF',
    field: 'nr_cpf',
    // 999.999.999-99 / Pseudonimizado, 123***
  },
  {
    headerName: 'Nr. CNS',
    field: 'nr_cns',
    // Pseudonimizado, 123***
  },
  {
    headerName: 'Cód. beneficiário',
    field: 'cd_beneficiario',
  },
  {
    headerName: 'Beneficiário',
    field: 'nm_beneficiario',
    // Pseudonimizado, BET***
  },
  {
    headerName: 'Mãe',
    field: 'nm_mae',
    // Pseudonimizado, MAR***
  },
  {
    headerName: 'Sexo',
    field: 'nm_id_sexo',
    // Domínio: dm_sexo
  },
  {
    headerName: 'Dt. nascimento',
    field: 'dt_nascimento',
    type: 'date',
  },
  {
    headerName: 'Nr. registro plano',
    field: 'nr_registro_plano',
  },
  {
    headerName: 'Relação de dependência (ID)',
    field: 'tp_relacao_dependencia',
    hide: true,
  },
  {
    headerName: 'Relação de dependência',
    field: 'nm_tp_relacao_dependencia',
    // Domínio: SIB_RELACAO_DEPENDENCIA
  },
  {
    headerName: 'Situação (ID)',
    field: 'id_situacao',
    hide: true,
  },
  {
    headerName: 'Situação',
    field: 'nm_id_situacao',
    // Domínio: CFG_SITUACAO_ATIVO_INATIVO
  },
  {
    headerName: 'Cód. município',
    field: 'cd_municipio_semdv',
  },
  {
    headerName: 'Município',
    field: 'nm_municipio',
  },
  {
    headerName: 'Estado',
    field: 'sg_uf',
  },
  {
    headerName: 'Dt. atualização',
    field: 'dt_atualizacao',
    type: 'date',
  },
  {
    headerName: 'Dt. contratação',
    field: 'dt_contratacao',
    type: 'date',
  },
  {
    headerName: 'Dt. reativação',
    field: 'dt_reativacao',
    type: 'date',
  },
  {
    headerName: 'Dt. cancelamento',
    field: 'dt_cancelamento',
    type: 'date',
  },
];

const COLUMNS_DIALOG_PRESTADOR_CNES: ColDef[] = [
  {
    headerName: 'Nr. CNES',
    field: 'nr_cnes',
  },
  {
    headerName: 'Nome fantasia',
    field: 'nm_fantasia',
  },
  {
    headerName: 'Nome empresarial',
    field: 'nm_empresarial',
  },
  {
    headerName: 'Tipo pessoa',
    field: 'tp_pessoa',
  },
  {
    headerName: 'CPF/CNPJ',
    field: 'nr_cpf_cnpj',
    type: 'cpfCnpj',
  },
  {
    headerName: 'Situação ativo (ID)',
    field: 'ib_situacao',
    hide: true,
  },
  {
    headerName: 'Situação ativo',
    field: 'nm_ib_situacao',
  },
  {
    headerName: 'Cód. tipo estabelecimento',
    field: 'cd_tipo_estabelecimento',
  },
  {
    headerName: 'Descrição tipo estabelecimento',
    field: 'ds_tipo_estabelecimento',
  },
  {
    headerName: 'Cód. natureza jurídica',
    field: 'cd_natureza_juridica',
  },
  {
    headerName: 'Descrição natureza jurídica',
    field: 'ds_natureza_juridica',
  },
  {
    headerName: 'CEP',
    field: 'nr_cep',
  },
  /*
  {
    headerName: 'Logradouro',
    field: 'ds_logradouro',
  },
  {
    headerName: 'Nr. Logradouro',
    field: 'nr_logradouro',
  },
  {
    headerName: 'Complemento',
    field: 'ds_complemento',
  },
  {
    headerName: 'Bairro',
    field: 'ds_bairro',
  },
  */
  {
    headerName: 'Cód. município',
    field: 'cd_municipio',
  },
  {
    headerName: 'Nome município',
    field: 'nm_municipio',
  },
  {
    headerName: 'UF',
    field: 'sg_uf',
  },
  {
    headerName: 'Urgência/Emergência (ID)',
    field: 'ib_urgencia_emergencia_24h',
    hide: true,
  },
  {
    headerName: 'Urgência/Emergência',
    field: 'nm_ib_urgencia_emergencia_24h',
  },
];

const COLUMNS_DIALOG_PRESTADOR_RPS: ColDef[] = [
  {
    headerName: 'Nr. competência',
    field: 'nr_competencia',
    type: 'competencia',
    width: 100,
  },
  {
    headerName: 'Rede hospitalar (ID)',
    field: 'ib_rede_hospitalar',
    hide: true,
  },
  {
    headerName: 'Rede hospitalar',
    field: 'nm_ib_rede_hospitalar',
  },
  {
    headerName: 'Nr. CPF/CNPJ',
    field: 'nr_cpf_cnpj',
    type: 'cpfCnpj',
  },
  {
    headerName: 'Nr. CNES',
    field: 'nr_cnes',
  },
  {
    headerName: 'Nr. prestador',
    field: 'nr_prestador_ops',
  },
  {
    headerName: 'Nome do prestador',
    field: 'nm_prestador',
  },
  {
    headerName: 'Cód. município',
    field: 'cd_municipio_semdv',
  },
  {
    headerName: 'Nome município',
    field: 'nm_municipio',
  },
  {
    headerName: 'UF',
    field: 'sg_uf',
  },
  {
    headerName: 'Classificação (ID)',
    field: 'id_classificacao',
    hide: true,
  },
  {
    headerName: 'Classificação',
    field: 'nm_id_classificacao',
  },
  {
    headerName: 'Urgência/Emergência (ID)',
    field: 'sn_urgencia_emergencia',
    hide: true,
  },
  {
    headerName: 'Urgência/Emergência',
    field: 'nm_sn_urgencia_emergencia',
  },
  {
    headerName: 'Tipo relação operadora (ID)',
    field: 'tp_relacao_operadora',
    hide: true,
  },
  {
    headerName: 'Tipo relação operadora',
    field: 'nm_tp_relacao_operadora',
  },
  {
    headerName: 'Tipo contratualização (ID)',
    field: 'tp_contratualizacao',
    hide: true,
  },
  {
    headerName: 'Tipo contratualização',
    field: 'nm_tp_contratualizacao',
  },
  {
    headerName: 'Tipo disponibilidade (ID)',
    field: 'tp_disponibilidade',
    hide: true,
  },
  {
    headerName: 'Tipo disponibilidade',
    field: 'nm_tp_disponibilidade',
  },
  {
    headerName: 'Data início vínculo',
    field: 'dt_vinculo_inicio',
    type: 'date',
  },
  {
    headerName: 'Data fim vínculo',
    field: 'dt_vinculo_fim',
    type: 'date',
  },
  {
    headerName: 'Cód. vínculo rede',
    field: 'cd_vinculo_rede',
  },
  {
    headerName: 'Cód. plano ANS',
    field: 'cd_plano_ans',
  },
  {
    headerName: 'Nr. registro plano',
    field: 'nr_registro_plano',
  },
];

const COLUMNS_DIALOG_PRESTADOR_MEDICOS: ColDef[] = [
  {
    headerName: 'Nr. CPF',
    field: 'nr_cpf',
    type: 'cpf',
  },
  {
    headerName: 'Nome do médico',
    field: 'nm_medico',
  },
  {
    headerName: 'Nr. CRM',
    field: 'nr_crm',
  },
  {
    headerName: 'UF',
    field: 'sg_uf',
    maxWidth: 100,
  },
  {
    headerName: 'Cód. situação (ID)',
    field: 'cd_situacao',
    hide: true,
  },
  {
    headerName: 'Cód. situação',
    field: 'nm_cd_situacao',
  },
  {
    headerName: 'Cód. CBO',
    field: 'cd_cbo',
    maxWidth: 150,
  },
  {
    headerName: 'Descrição CBO',
    field: 'ds_titulo_cbo',
  },
];

@Component({
    selector: 'bl-detalhes-guia',
    templateUrl: './detalhes-guia.component.html',
    styleUrls: ['./detalhes-guia.component.scss'],
    standalone: false
})
export class DetalhesGuiaComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('agGridModeloRemuneracao') agGridModeloRemuneracao: AgGridAngular<RowDetalheModeloRemuneracao>;
  @ViewChild('agGridLancamentosGuia') agGridLancamentosGuia: AgGridAngular<RowDetalheLancamentos>;
  @ViewChild('agGridCriticasGuia') agGridCriticasGuia: AgGridAngular<RowDetalheCriticasGuia>;
  @ViewChild('agGridMotivosRejeicao') agGridMotivosRejeicao: AgGridAngular<RowDetalheMotivosRejeicao>;
  @ViewChild('agGridItensGuias') agGridItensGuias: AgGridAngular<RowDetalheItemGuia>;

  @ViewChild('agGridDialogBeneficiario') agGridDialogBeneficiario: AgGridAngular<RowDialogBeneficiario>;

  @ViewChild('agGridDialogPrestadorCnes') agGridDialogPrestadorCnes: AgGridAngular<RowDialogPrestadorCnes>;
  @ViewChild('agGridDialogPrestadorRps') agGridDialogPrestadorRps: AgGridAngular<RowDialogPrestadorRps>;
  @ViewChild('agGridDialogPrestadorMedicos') agGridDialogPrestadorMedicos: AgGridAngular<RowDialogPrestadorMedicos>;
  @ViewChild('agGridDialogAtributosQualidade') agGridDialogAtributosQualidade: AgGridAngular<RowAtributosQualidade>;

  gridOptionsModeloRemuneracao: GridOptions<RowDetalheModeloRemuneracao>;
  gridOptionsLancamentosGuia: GridOptions<RowDetalheLancamentos>;
  gridOptionsCriticasGuia: GridOptions<RowDetalheCriticasGuia>;
  gridOptionsMotivosRejeicao: GridOptions<RowDetalheMotivosRejeicao>;
  gridOptionsItensGuias: GridOptions<RowDetalheItemGuia>;

  gridOptionsDialogBeneficiario: GridOptions<RowDialogBeneficiario>;

  gridOptionsDialogPrestadorCnes: GridOptions<RowDialogPrestadorCnes>;
  gridOptionsDialogPrestadorRps: GridOptions<RowDialogPrestadorRps>;
  gridOptionsDialogPrestadorMedicos: GridOptions<RowDialogPrestadorMedicos>;

  @Input()
  operadora: string;

  @Input()
  cdGuiaMonitor: string;

  @Input()
  nrCnpjCpfExec: string;

  @Input()
  nrCnesExec: string;

  @Input()
  nrRegistroPlano: string;

  @Input()
  height: string;

  @Output()
  onChange = new EventEmitter<string>();

  private readonly TAB_DADOS_PRINCIPAIS = 0;
  private readonly TAB_DADOS_INTERNACAO = 1;
  private readonly TAB_VALORES_PROCESSAMENTO = 2;
  private readonly TAB_ITENS_PROCEDIMENTOS = 3;
  private readonly TAB_CRITICAS_REJEICAO = 4;
  private readonly TAB_MOVIMENTACAO = 5;

  // Estado por aba — cada aba carrega sob demanda (lazy) e fica em cache na sessão do modal.
  dadosPrincipais: RowDadosPrincipais;
  dadosPrincipaisOriginal: RowDadosPrincipais;
  dadosInternacao: RowDadosInternacao;
  valoresProcessamento: RowValoresProcessamento;
  itensGuias: RowDetalheItemGuia[];
  criticasGuia: RowDetalheCriticasGuia[];
  motivosRejeicao: RowDetalheMotivosRejeicao[];

  // Índices das abas já carregadas (invalidado ao trocar operadora/cdGuiaMonitor).
  private loaded = new Set<number>();

  dialogBeneficiarioData: RowDialogBeneficiario[];
  dialogBeneficiarioDataOriginal: RowDialogBeneficiario[];
  dialogBeneficiarioDisplay: boolean = false;

  dialogPrestadorData: RowDialogPrestador;
  dialogPrestadorDisplay: boolean = false;

  colDefsMovimentacaoTransformada: ColDef[] = [];
  rowMovimentacaoGuiaTransformada: RowMovimentacaoTransformada[] = [];

  detalhePrestadorCodigo: string;
  detalhePrestadorCnpjCpf: string;
  detalhePrestadorCnes: string;
  detalhePrestadorRegistroPlano: string;

  operadoraWorkspace: Operadora | VinculoUsuarioOperadora;
  perfil: UsuarioPerfil;

  pseudonimizarAction: boolean = true;
  pseudonimizarActionVisible: boolean = true;
  showDadosBeneficiarioButton: boolean = false;
  showAbaDadosRPS: boolean = false;

  innerHeight: string;

  activeIndex = 0;

  constructor(
    private permissaoService: PermissaoService,
    private simuladorGoApi: SimuladorGoApi,
    private geralService: GeralService,
    private decimalPipe: DecimalPipe,
    private notificationService: NotificationService,
    private cpfCnpjPipe: CpfCnpjPipe,
    private datePipe: DatePipe,
    private store: Store,
  ) {}

  ngOnInit(): void {
    combineLatest([
      this.permissaoService.checkAcessoFeature(FUNCIONALIDADE.DESLIGAR_PSEUDONIMIZACAO),
      // Habilitada só em PREMIUM e QUALIFICA; o administrador a recebe pelo papel.
      this.permissaoService.checkAcessoFeature(FUNCIONALIDADE.DADOS_RPS),
      this.store.select(OperadorasSelectors.selectWorkspaceOperadora),
      this.store.select(UserProfileSelectors.selectUserIdssProfile),
    ])
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(([podeDesligarPseudonimizacao, podeVerDadosRps, operadora, perfil]) => {
        this.operadoraWorkspace = operadora;
        this.perfil = perfil;

        this.showDadosBeneficiarioButton = podeVerDadosRps;
        this.showAbaDadosRPS = podeVerDadosRps;

        if (operadora.ib_dado_sensivel_pseudonimizado) {
          this.pseudonimizarActionVisible = false;
        } else {
          const vinculo = this.operadoraWorkspace as VinculoUsuarioOperadora;
          this.pseudonimizarActionVisible = podeDesligarPseudonimizacao || !vinculo.ib_dado_sensivel_pseudonimizado_usuario;
        }
        this.configureGrid();
      });
  }

  private configureGrid() {
    const columnDefsLanctoGuia = COLUMNS_LANCAMENTOS_GUIA;
    const colGuiaMonitor = columnDefsLanctoGuia.find(col => col.field == 'cd_guia_monitor');
    colGuiaMonitor.cellRendererParams['actionFunction'] = value => this.onChange.next(value);
    colGuiaMonitor.cellRendererParams['showActionCallback'] = value => this.cdGuiaMonitor != value;

    this.gridOptionsModeloRemuneracao = buildGridOptions<RowDetalheModeloRemuneracao>(
      {
        columnDefs: COLUMNS_MODELO_REMUNERACAO,
        pipes: {
          decimal: this.decimalPipe,
        },
      },
      {
        sideBar: null,
        statusBar: null,
      },
    );
    this.gridOptionsLancamentosGuia = buildGridOptions<RowDetalheLancamentos>(
      {
        columnDefs: COLUMNS_LANCAMENTOS_GUIA,
        pipes: {
          decimal: this.decimalPipe,
        },
      },
      {
        sideBar: null,
        statusBar: null,
      },
    );
    this.gridOptionsCriticasGuia = buildGridOptions<RowDetalheCriticasGuia>(
      {
        columnDefs: COLUMNS_CRITICAS_GUIA,
      },
      {
        sideBar: null,
        statusBar: null,
        grandTotalRow: null,
      },
    );
    this.gridOptionsMotivosRejeicao = buildGridOptions<RowDetalheMotivosRejeicao>(
      {
        columnDefs: COLUMNS_MOTIVOS_REJEICAO,
      },
      {
        sideBar: null,
        statusBar: null,
        grandTotalRow: null,
      },
    );
    this.gridOptionsItensGuias = buildGridOptions<RowDetalheItemGuia>(
      {
        columnDefs: COLUMNS_ITENS_GUIAS,
        pipes: {
          decimal: this.decimalPipe,
          cpfCnpj: this.cpfCnpjPipe,
        },
      },
      {
        groupDefaultExpanded: 0,
        grandTotalRow: null,
        masterDetail: true,
        detailCellRenderer: ItemDetalheCellRenderer,
      },
    );
    this.gridOptionsDialogBeneficiario = buildGridOptions<RowDialogBeneficiario>(
      {
        columnDefs: COLUMNS_DIALOG_BENEFICIARIO,
        pipes: {
          cpfCnpj: this.cpfCnpjPipe,
          date: this.datePipe,
        },
      },
      {
        groupDefaultExpanded: 0,
        grandTotalRow: null,
      },
    );
    this.gridOptionsDialogPrestadorCnes = buildGridOptions<RowDialogPrestadorCnes>(
      {
        columnDefs: COLUMNS_DIALOG_PRESTADOR_CNES,
        pipes: {
          cpfCnpj: this.cpfCnpjPipe,
          date: this.datePipe,
        },
      },
      {
        sideBar: null,
        statusBar: null,
        grandTotalRow: null,
      },
    );
    this.gridOptionsDialogPrestadorRps = buildGridOptions<RowDialogPrestadorRps>(
      {
        columnDefs: COLUMNS_DIALOG_PRESTADOR_RPS,
        pipes: {
          cpfCnpj: this.cpfCnpjPipe,
          date: this.datePipe,
        },
      },
      {
        sideBar: null,
        statusBar: null,
        grandTotalRow: null,
      },
    );
    this.gridOptionsDialogPrestadorMedicos = buildGridOptions<RowDialogPrestadorMedicos>(
      {
        columnDefs: COLUMNS_DIALOG_PRESTADOR_MEDICOS,
        pipes: {
          cpfCnpj: this.cpfCnpjPipe,
          date: this.datePipe,
        },
      },
      {
        sideBar: null,
        statusBar: null,
        grandTotalRow: null,
      },
    );
  }

  private normalize = (campo: string, valor: any) => {
    if (valor == null) return '';

    if (campo in DOMINIOS_MAPEADOS) {
      const nome = this.geralService.buildNomeDeDominio(valor, DOMINIOS_MAPEADOS[campo]);
      return (nome ?? valor).toString().trim();
    }

    if (campo === 'ib_existe_mon_guia') return valor === 0 ? 'Sim' : valor === 1 ? 'Não' : String(valor);

    if ((CAMPOS_DE_DATA as readonly string[]).includes(campo)) {
      const d = new Date(valor);
      // A API devolve datas de calendário ("YYYY-MM-DD") ou ISO com Z, ambas meia-noite UTC.
      // Formatamos em UTC para evitar o (-1 dia) no fuso local.
      return isNaN(d.getTime()) ? String(valor) : d.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
    }

    return String(valor).trim();
  };

  private transformarMovimentacao(dados: DetalheMovimentacaoGuia[]): RowMovimentacaoTransformada[] {
    return CAMPOS.map(campo => ({
      grupo: GRUPOS_MOCK[campo] ?? 'Outro',
      campo,
      valores: dados.map(item => item[campo as keyof DetalheMovimentacaoGuia] ?? null),
    }));
  }

  private buildColDefsMovimentacao(data: RowMovimentacaoTransformada[]): ColDef[] {
    const existRow = data.find(d => d.campo === 'ib_existe_mon_guia');
    const existValues = existRow ? existRow.valores : [];
    const maxValores = Math.max(...data.map(d => d.valores.length));
    const linhaCd = data.find(d => d.campo === 'cd_guia_monitor');
    const idxAtivo = linhaCd ? linhaCd.valores.findIndex(v => String(v) === String(this.cdGuiaMonitor)) : -1;

    const colDefs: ColDef[] = [
      {
        headerName: 'Grupo',
        field: 'grupo',
        width: 150,
        filter: true,
        valueFormatter: params => {
          const currentRowIndex = params.node?.rowIndex;
          const currentValue = params.value;

          const previousNode = params.api?.getDisplayedRowAtIndex(currentRowIndex - 1);
          const previousValue = previousNode?.data?.grupo;

          return currentValue !== previousValue ? currentValue : '';
        },
      },
      {
        headerName: 'Campo',
        field: 'campo',
        valueFormatter: p => CAMPO_ALIASES[p.value as keyof typeof CAMPO_ALIASES] ?? String(p.value),
        width: 350,
        filter: true,
        cellClass: 'campo-bold',
      },
    ];

    for (let i = 0; i < maxValores; i++) {
      const isAtiva = i === idxAtivo;

      const colStateClass = isAtiva ? 'col-ativa' : 'col-inativa';

      const isDescartada =
        String(existValues?.[i]) === '0' || this.normalize('ib_existe_mon_guia', existValues?.[i]) === 'Sim';

      const weakTextClass = isDescartada ? 'mov-descartada' : '';

      const canClick = !!existValues?.[i];

      colDefs.push({
        headerName: `Movimentação ${i + 1}`,
        field: `valores[${i}]`,
        headerClass: `${colStateClass} ${weakTextClass}`,
        cellClass: `${colStateClass} ${weakTextClass}`,
        width: 300,
        filter: true,

        cellClassRules:
          !isAtiva && idxAtivo !== -1
            ? {
                'cell-valor-diferente': p => {
                  if (isDescartada) return false;
                  const c = p.data?.campo;
                  if (!c || c === 'cd_guia_monitor') return false;
                  return this.normalize(c, p.data?.valores?.[i]) !== this.normalize(c, p.data?.valores?.[idxAtivo]);
                },
              }
            : {},

        tooltipValueGetter: p => {
          const c = p.data?.campo;
          if (!c) return null;

          if (isDescartada || i === idxAtivo || idxAtivo === -1 || c === 'cd_guia_monitor') return null;

          const atual = this.normalize(c, p.data?.valores?.[i]);
          const ativa = this.normalize(c, p.data?.valores?.[idxAtivo]);

          return atual !== ativa ? `Alterado em relação à Movimentação ${idxAtivo + 1}` : null;
        },

        cellRendererSelector: params => {
          if (params.data?.campo !== 'cd_guia_monitor') return undefined;
          return {
            component: AgGridActionCellRenderer,
            params: {
              canClick,
              actionFunction: (cd: string) => this.onChange.next(cd),
              showActionCallback: (cd: string) => String(cd) !== String(this.cdGuiaMonitor),
            } as AgGridActionCellParams,
          };
        },

        valueGetter: p => this.normalize(p.data?.campo, p.data?.valores?.[i]),
      });
    }

    return colDefs;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.innerHeight = `calc(${this.height ?? '90vh'} - ${HEIGHT_PADDING})`;

    if (!this.operadora || !this.cdGuiaMonitor) {
      return;
    }

    if (changes['operadora'] || changes['cdGuiaMonitor']) {
      this.activeIndex = 0;
      this.resetState();
    }

    // Aba 1 (preenche o cabeçalho fixo) e aba 5 (contadores de críticas/motivos aparecem no header)
    // carregam imediatamente; demais são lazy.
    this.loadTab(this.TAB_DADOS_PRINCIPAIS);
    this.loadTab(this.TAB_CRITICAS_REJEICAO);
  }

  private resetState(): void {
    this.loaded.clear();
    this.dadosPrincipais = null;
    this.dadosPrincipaisOriginal = null;
    this.dadosInternacao = null;
    this.valoresProcessamento = null;
    this.itensGuias = null;
    this.criticasGuia = null;
    this.motivosRejeicao = null;
    this.rowMovimentacaoGuiaTransformada = [];
    this.colDefsMovimentacaoTransformada = [];
  }

  onTabChange(index: number): void {
    this.loadTab(index);
  }

  private loadTab(index: number): void {
    if (this.loaded.has(index)) {
      return;
    }
    switch (index) {
      case this.TAB_DADOS_PRINCIPAIS:
        this.loadDadosPrincipais();
        break;
      case this.TAB_DADOS_INTERNACAO:
        this.loadDadosInternacao();
        break;
      case this.TAB_VALORES_PROCESSAMENTO:
        this.loadValoresProcessamento();
        break;
      case this.TAB_ITENS_PROCEDIMENTOS:
        this.loadItensProcedimentos();
        break;
      case this.TAB_CRITICAS_REJEICAO:
        this.loadCriticasRejeicao();
        break;
      case this.TAB_MOVIMENTACAO:
        this.loadMovimentacao();
        break;
    }
  }

  private loadDadosPrincipais(): void {
    this.loaded.add(this.TAB_DADOS_PRINCIPAIS);
    this.simuladorGoApi
      .fetchDetalheGuiaDadosPrincipais({ regans: this.operadora, cd_guia_monitor: this.cdGuiaMonitor })
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe({
        next: item => {
          if (!item) {
            this.dadosPrincipaisOriginal = null;
            this.dadosPrincipais = null;
            return;
          }
          this.dadosPrincipaisOriginal = {
            ...item,
            nm_tp_guia: this.geralService.buildNomeDeDominio(item.tp_guia, 'dm_tipoEventoMonitoramento'),
            nm_id_origem_guia: this.geralService.buildNomeDeDominio(item.id_origem_guia, 'dm_origemEventoAtencaoSaude'),
            nm_st_guia_ans: this.geralService.buildNomeDeDominio(item.st_guia_ans, 'MON_GUIA_SITUACAO_ANS'),
            nm_id_cnpj_cpf_exec: this.geralService.buildNomeDeDominio(item.id_cnpj_cpf_exec, 'MON_ID_CNPJ_CPF'),
            nm_tp_prestador: this.geralService.buildNomeDeDominio(item.tp_prestador, 'MON_TIPO_PRESTADOR'),
            nm_id_forma_envio_exec: this.geralService.buildNomeDeDominio(item.id_forma_envio_exec, 'dm_formaEnvio'),
            nm_tp_atendimento: this.geralService.buildNomeDeDominio(item.tp_atendimento, 'dm_tipoAtendimento'),
            nm_id_regime_atendimento: this.geralService.buildNomeDeDominio(
              item.id_regime_atendimento,
              'dm_regimeAtendimento',
            ),
            nm_id_saude_ocupacional: this.geralService.buildNomeDeDominio(
              item.id_saude_ocupacional,
              'dmSaudeOcupacional',
            ),
            nm_tp_consulta: this.geralService.buildNomeDeDominio(item.tp_consulta, 'dm_tipoConsulta'),
            nm_ib_recem_nato: this.geralService.buildNomeDeDominio(item.ib_recem_nato, 'dm_simNao'),
            nm_id_carater_atendimento: this.geralService.buildNomeDeDominio(
              item.id_carater_atendimento,
              'dm_caraterMonitoramento',
            ),
            nm_id_motivo_saida: this.geralService.buildNomeDeDominio(item.id_motivo_saida, 'dm_motivoSaida'),
            nm_id_acidente: this.geralService.buildNomeDeDominio(item.id_acidente, 'dm_indicadorAcidente'),
            nm_id_sexo: this.geralService.buildNomeDeDominio(item.id_sexo, 'dm_sexo'),
            nm_tp_atendimento_intermediaria: this.geralService.buildNomeDeDominio(
              item.tp_atendimento_intermediaria,
              'dm_tipoAtendimentoIntermediario',
            ),
            nm_id_situacao_lote: this.geralService.buildNomeDeDominio(item.id_situacao_lote, 'MON_LOTE_SITUACAO'),
          };
          this.pseudonimizarData();
        },
        error: error => {
          this.loaded.delete(this.TAB_DADOS_PRINCIPAIS);
          this.notificationService.handleError(error);
        },
      });
  }

  private loadDadosInternacao(): void {
    this.loaded.add(this.TAB_DADOS_INTERNACAO);
    this.simuladorGoApi
      .fetchDetalheGuiaDadosInternacao({ regans: this.operadora, cd_guia_monitor: this.cdGuiaMonitor })
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe({
        next: item => {
          this.dadosInternacao = item
            ? {
                ...item,
                nm_tp_internacao: this.geralService.buildNomeDeDominio(
                  item.tp_internacao,
                  'dm_tipoInternacaoMonitoramento',
                ),
              }
            : null;
        },
        error: error => {
          this.loaded.delete(this.TAB_DADOS_INTERNACAO);
          this.notificationService.handleError(error);
        },
      });
  }

  private loadValoresProcessamento(): void {
    this.loaded.add(this.TAB_VALORES_PROCESSAMENTO);
    this.simuladorGoApi
      .fetchDetalheGuiaValoresProcessamento({ regans: this.operadora, cd_guia_monitor: this.cdGuiaMonitor })
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe({
        next: item => {
          if (!item) {
            this.valoresProcessamento = null;
            return;
          }
          this.valoresProcessamento = {
            ...item,
            nm_tp_registro: this.geralService.buildNomeDeDominio(item.tp_registro, 'dm_statusTransacaoMonitor'),
            nm_id_situacao_registro: this.geralService.buildNomeDeDominio(
              item.id_situacao_registro,
              'MON_REGISTRO_SITUACAO',
            ),
            nm_st_guia_ans: this.geralService.buildNomeDeDominio(item.st_guia_ans, 'MON_GUIA_SITUACAO_ANS'),
            row_modelo_remuneracao: (item.modelo_remuneracao ?? []).map(subitem => ({
              ...subitem,
              nm_id_modelo_remuneracao: this.geralService.buildNomeDeDominio(
                subitem.id_modelo_remuneracao,
                'dm_formaRemuneracao',
              ),
            })),
          };
        },
        error: error => {
          this.loaded.delete(this.TAB_VALORES_PROCESSAMENTO);
          this.notificationService.handleError(error);
        },
      });
  }

  private loadItensProcedimentos(): void {
    this.loaded.add(this.TAB_ITENS_PROCEDIMENTOS);
    this.simuladorGoApi
      .fetchDetalheGuiaItensProcedimentos({ regans: this.operadora, cd_guia_monitor: this.cdGuiaMonitor })
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe({
        next: response => {
          this.itensGuias = (response.items ?? []).map(subitem => {
            const detalhe_pacote = subitem.detalhe_pacote ?? [];
            const criticas_item = subitem.criticas_item ?? [];
            const motivos_rejeicao_item = subitem.motivos_rejeicao_item ?? [];

            const row_item_guia: RowDetalheItemGuia = {
              ...subitem,

              nm_cd_tabela_tuss: this.geralService.buildNomeDeDominio(subitem.cd_tabela_tuss, 'dm_tabelaMonitor'),
              nm_id_face_dente: this.geralService.buildNomeDeDominio(subitem.id_face_dente, 'dm_face'),
              nm_cd_unidade_medida: this.geralService.buildNomeDeDominio(subitem.cd_unidade_medida, 'dm_unidadeMedida'),

              row_detalhe_pacote: detalhe_pacote.map(pacote => ({
                ...pacote,
                nm_id_forma_envio: this.geralService.buildNomeDeDominio(pacote.id_forma_envio, 'TUSS_FORMA_ENVIO_ANS'),
                nm_cd_tabela_tuss: this.geralService.buildNomeDeDominio(pacote.cd_tabela_tuss, 'dm_tabelaPacote'),
                nm_cd_unidade_medida: this.geralService.buildNomeDeDominio(
                  pacote.cd_unidade_medida,
                  'dm_unidadeMedida',
                ),
              })),

              row_criticas_item: criticas_item.map(critica => ({
                ...critica,
                nm_tp_critica: this.geralService.buildNomeDeDominio(critica.tp_critica, 'CFG_TIPO_FILTRO'),
                nm_tp_impacto: this.geralService.buildNomeDeDominio(critica.tp_impacto, 'MON_TIPO_IMPACTO'),
              })),

              row_motivos_rejeicao: motivos_rejeicao_item.map(motivo => ({ ...motivo })),
            };
            return row_item_guia;
          });
        },
        error: error => {
          this.loaded.delete(this.TAB_ITENS_PROCEDIMENTOS);
          this.notificationService.handleError(error);
        },
      });
  }

  private loadCriticasRejeicao(): void {
    this.loaded.add(this.TAB_CRITICAS_REJEICAO);
    this.simuladorGoApi
      .fetchDetalheGuiaCriticas({ regans: this.operadora, cd_guia_monitor: this.cdGuiaMonitor })
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe({
        next: response => {
          this.criticasGuia = (response.items ?? []).map(subitem => ({
            ...subitem,
            nm_tp_critica: this.geralService.buildNomeDeDominio(subitem.tp_critica, 'CFG_TIPO_FILTRO'),
            nm_tp_impacto: this.geralService.buildNomeDeDominio(subitem.tp_impacto, 'MON_TIPO_IMPACTO'),
          }));
        },
        error: error => {
          this.loaded.delete(this.TAB_CRITICAS_REJEICAO);
          this.notificationService.handleError(error);
        },
      });

    this.simuladorGoApi
      .fetchDetalheGuiaMotivosRejeicao({ regans: this.operadora, cd_guia_monitor: this.cdGuiaMonitor })
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe({
        next: response => {
          this.motivosRejeicao = (response.items ?? []).map(subitem => ({ ...subitem }));
        },
        error: error => {
          this.loaded.delete(this.TAB_CRITICAS_REJEICAO);
          this.notificationService.handleError(error);
        },
      });
  }

  private loadMovimentacao(): void {
    this.loaded.add(this.TAB_MOVIMENTACAO);
    this.simuladorGoApi
      .fetchMovimentacaoGuia({ regans: this.operadora, cd_guia_monitor: this.cdGuiaMonitor })
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe({
        next: response => {
          if (!response.items?.length) {
            this.rowMovimentacaoGuiaTransformada = null;
            return;
          }
          const movimentacoes = response.items ?? [];
          this.rowMovimentacaoGuiaTransformada = this.transformarMovimentacao(movimentacoes);
          this.colDefsMovimentacaoTransformada = this.buildColDefsMovimentacao(this.rowMovimentacaoGuiaTransformada);
        },
        error: error => {
          this.loaded.delete(this.TAB_MOVIMENTACAO);
          this.notificationService.handleError(error);
        },
      });
  }

  showDadosBeneficiario() {
    // console.log('[Ben] showDadosBeneficiario');
    this.dialogBeneficiarioDisplay = true;

    this.agGridDialogBeneficiario?.api?.setGridOption('loading', true);
    this.simuladorGoApi.fetchDetalhesBeneficiario({ regans: this.operadora, nr_guia_monitor: this.cdGuiaMonitor }).subscribe({
      next: response => {
        if (!response.items?.length) {
          this.dialogBeneficiarioData = [];
          this.dialogBeneficiarioDataOriginal = [];
          return;
        }

        this.dialogBeneficiarioDataOriginal = response.items.map(item => {
          let row: RowDialogBeneficiario = {
            ...item,

            nm_tp_relacao_dependencia: this.geralService.buildNomeDeDominio(
              item.tp_relacao_dependencia,
              'SIB_RELACAO_DEPENDENCIA',
            ),
            nm_id_situacao: this.geralService.buildNomeDeDominio(item.id_situacao, 'CFG_SITUACAO_ATIVO_INATIVO'),
            nm_id_sexo: this.geralService.buildNomeDeDominio(item.id_sexo, 'dm_sexo'),
          };

          return row;
        });

        this.pseudonimizarData();
      },
      error: error => this.notificationService.handleError(error),
      complete: () => this.agGridDialogBeneficiario?.api?.setGridOption('loading', false),
    });
  }

  onDialogBeneficiarioSearch(value: string) {
    this.agGridDialogBeneficiario.api.updateGridOptions({ quickFilterText: value });
  }

  dialogBeneficiarioExportExcel(): void {
    const fileNamePrefix = `Beneficiario`;

    const headers = ['Dados do Beneficiário'];

    exportGridAsExcel({
      api: this.agGridDialogBeneficiario.api,
      headers,
      fileNamePrefix,
    });
  }

  showDetalhePrestadorFor(row: RowDadosPrincipais) {
    this.dialogPrestadorDisplay = true;
    this.detalhePrestadorCnpjCpf = row.nr_cnpj_cpf_exec;
    this.detalhePrestadorCodigo = row.cd_guia_monitor;
    this.detalhePrestadorCnes = row.nr_cnes_exec;
    this.detalhePrestadorRegistroPlano = row.nr_registro_plano;
  }

  dialogPrestadorExportExcel(): void {
    const mainHeaders = [
      'DADOS DO PRESTADOR',
      `${this.operadoraWorkspace?.nm_operadora} - ${this.operadora}`,
      `Cód. lancto: ${this.dialogPrestadorData?.cd_guia_monitor}`,
      `CNPJ/CPF executante: ${this.cpfCnpjPipe.transform(this.dialogPrestadorData?.nr_cnpj_cpf_exec)}`,
      `CNES executante: ${this.dialogPrestadorData?.nr_cnes_exec}`,
      `Nr. registro plano: ${this.dialogPrestadorData?.nr_registro_plano}`,
      'Exportado pelo Gestor IDSS em: ' + new Date().toLocaleString('pt-BR'),
    ];

    const mainSheet = this.agGridDialogPrestadorCnes.api.getSheetDataForExcel({
      sheetName: `Dados do CNES`,
      prependContent: AgFilterUtils.buildFilterRows(this.agGridDialogPrestadorCnes.api, [
        ...mainHeaders,
        '',
        'Dados do CNES',
      ]),
    });

    const secondSheet = this.agGridDialogPrestadorRps.api.getSheetDataForExcel({
      sheetName: 'Dados do RPS',
      prependContent: AgFilterUtils.buildFilterRows(this.agGridDialogPrestadorRps.api, [
        ...mainHeaders,
        '',
        'Dados do RPS',
      ]),
    });

    const thirdSheet = this.agGridDialogPrestadorMedicos.api.getSheetDataForExcel({
      sheetName: 'Dados do Médico',
      prependContent: AgFilterUtils.buildFilterRows(this.agGridDialogPrestadorMedicos.api, [
        ...mainHeaders,
        '',
        'Dados do médico',
      ]),
    });

    const fourthSheet = this.agGridDialogAtributosQualidade.api.getSheetDataForExcel({
      sheetName: 'Dados de Atributo de Qualidade',
      prependContent: AgFilterUtils.buildFilterRows(this.agGridDialogAtributosQualidade.api, [
        ...mainHeaders,
        '',
        'Dados de atributo de qualidade',
      ]),
    });

    const spreadsheets: string[] = [];
    spreadsheets.push(mainSheet);
    spreadsheets.push(secondSheet);
    spreadsheets.push(thirdSheet);
    spreadsheets.push(fourthSheet);

    exportMultipleSheetsAsExcel({
      data: spreadsheets,
      fileName: `Prestador-em-${isoDateNoPuncts()}.xlsx`,
    });
  }

  togglePseudonimizar() {
    this.pseudonimizarAction = !this.pseudonimizarAction;
    this.pseudonimizarData();
  }

  private pseudonimizarData() {
    // console.log('[Ben] pseudonimizarData');
    if (!this.pseudonimizarActionVisible || !this.pseudonimizarAction) {
      this.dadosPrincipais = this.dadosPrincipaisOriginal;
      this.dialogBeneficiarioData = this.dialogBeneficiarioDataOriginal;
      return;
    }

    this.dadosPrincipais = this.dadosPrincipaisOriginal
      ? {
          ...this.dadosPrincipaisOriginal,

          nr_cns: pseudonimizar(this.dadosPrincipaisOriginal.nr_cns),
          nr_cpf_beneficiario: pseudonimizar(this.dadosPrincipaisOriginal.nr_cpf_beneficiario),
          nr_cartao_beneficiario: pseudonimizar(this.dadosPrincipaisOriginal.nr_cartao_beneficiario),
        }
      : null;

    this.dialogBeneficiarioData = this.dialogBeneficiarioDataOriginal?.map(row => {
      return {
        ...row,

        nr_cpf: pseudonimizar(row.nr_cpf),
        nr_cns: pseudonimizar(row.nr_cns),
        nm_beneficiario: pseudonimizar(row.nm_beneficiario),
        nm_mae: pseudonimizar(row.nm_mae),
      };
    });
  }

  private ngDestroyed$ = new Subject<void>();
  ngOnDestroy() {
    this.ngDestroyed$.next();
    this.ngDestroyed$.complete();
  }
}

// Aba 1 — Dados principais.
export interface RowDadosPrincipais extends DetalheGuiaDadosPrincipais {
  nm_tp_guia: string;
  nm_id_origem_guia: string;
  nm_st_guia_ans: string;
  nm_id_cnpj_cpf_exec: string;
  nm_tp_prestador: string;
  nm_id_forma_envio_exec: string;
  nm_tp_atendimento: string;
  nm_id_regime_atendimento: string;
  nm_id_saude_ocupacional: string;
  nm_tp_consulta: string;
  nm_ib_recem_nato: string;
  nm_id_carater_atendimento: string;
  nm_id_motivo_saida: string;
  nm_id_acidente: string;
  nm_id_sexo: string;
  nm_tp_atendimento_intermediaria: string;
  nm_id_situacao_lote: string;
}

// Aba 2 — Dados da internação.
export interface RowDadosInternacao extends DetalheGuiaDadosInternacao {
  nm_tp_internacao: string;
}

// Aba 3 — Valores e processamento + sub-grid de modelos de remuneração.
export interface RowValoresProcessamento extends DetalheGuiaValoresProcessamento {
  nm_tp_registro: string;
  nm_id_situacao_registro: string;
  nm_st_guia_ans: string;

  row_modelo_remuneracao: RowDetalheModeloRemuneracao[];
}

export interface RowDetalheModeloRemuneracao extends DetalheGuiaModeloRemuneracao {
  nm_id_modelo_remuneracao: string;
}

export interface RowDetalheLancamentos extends DetalheGuiaLancamentosGuias {}

export interface RowDetalheCriticasGuia extends DetalheGuiaCriticasGuia {
  nm_tp_critica: string;
  nm_tp_impacto: string;
}

export interface RowDetalheMotivosRejeicao extends DetalheGuiaMotivosRejeicaoGuia {}

export interface RowDetalheItemGuia extends DetalheGuiaItensGuia {
  nm_cd_tabela_tuss: string;
  nm_id_face_dente: string;
  nm_cd_unidade_medida: string;

  row_detalhe_pacote: RowItemDetalhePacote[];
  row_criticas_item: RowItemCriticasItem[];
  row_motivos_rejeicao: RowItemMotivosRejeicao[];
}

export interface RowDetalheMovimentacaoGuia extends DetalheMovimentacaoGuia {}

interface RowMovimentacaoTransformada {
  grupo: string;
  campo: string;
  valores: (string | number | null)[];
}

export interface RowItemDetalhePacote extends DetalheGuiaItensGuiaDetalhePacote {
  nm_id_forma_envio: string;
  nm_cd_tabela_tuss: string;
  nm_cd_unidade_medida: string;
}

export interface RowItemCriticasItem extends DetalheGuiaItensGuiaCriticasItem {
  nm_tp_critica: string;
  nm_tp_impacto: string;
}

export interface RowItemMotivosRejeicao extends DetalheGuiaItensGuiaMotivosRejeicaoItem {}

interface RowDialogBeneficiario extends DetalheBeneficiario {
  nm_tp_relacao_dependencia: string;
  nm_id_situacao: string;
  nm_id_sexo: string;
}

interface RowDialogPrestador extends Partial<DetalhePrestador> {
  row_dados_cnes: RowDialogPrestadorCnes[];
  row_dados_rps: RowDialogPrestadorRps[];
  row_dados_medico: RowDialogPrestadorMedicos[];
}

interface RowDialogPrestadorCnes extends DetalhePrestadorCnes {
  nm_ib_urgencia_emergencia_24h: string;
  nm_ib_situacao: string;
}

interface RowDialogPrestadorRps extends DetalhePrestadorRps {
  nm_ib_rede_hospitalar: string;
  nm_id_classificacao: string;
  nm_sn_urgencia_emergencia: string;
  nm_tp_relacao_operadora: string;
  nm_tp_contratualizacao: string;
  nm_tp_disponibilidade: string;
}

interface RowDialogPrestadorMedicos extends DetalhePrestadorMedicos {
  nm_cd_situacao: string;
}

interface RowDialogAtributosQualidade extends DetalheAtributosQualidade {}
